import { useId, useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "~/lib/cn";
import { Button } from "./Button";

/**
 * Dashboard calendar — week strip + month grid + month navigation (T407).
 *
 * Replaces `WeekStrip`, which predated the design system (no tokens, arbitrary
 * type sizes, a non-canonical 680px breakpoint) and was never rendered anywhere.
 * Skin: the `.cal-*` block in `app/styles/app.css`, landed from
 * `_docs/02-handoffs/design_handoff_tfc/tokens/calendar.css`.
 *
 * This is where accomplishment lives (founder call, 2026-08-02): the phase card
 * carries the wait, this carries what she actually did. That ordering is why the
 * empty and sparse months are the states this component gets right first — a day
 * with no session is a whispered number with no box left unfilled, and a day that
 * has not arrived only drops ink rather than being drawn as pending. Nothing here
 * may read as debt.
 *
 * Cells are mute on purpose: no minutes, no counters, no badges. The detail lives
 * in `DaySessionSheet`, which the host opens from `onSelectDay`.
 *
 * PURE by contract — props in, callbacks out. No `useFetcher`, no data `useEffect`,
 * no service import: the month state and the request for another month belong to
 * the host. Day cells and month arrows compose the shared Button while retaining
 * the calendar's own geometry.
 */

/** One column of the seven-day strip. `sessionCount > 0` is the only tappable state. */
export type CalendarWeekDay = {
  /** Day of month, as shown in the mark. */
  day: number;
  /** Member-local `YYYY-MM-DD` — the key the week drill-in resolves against. */
  iso: string;
  year: number;
  /** 0-indexed, JS `Date` convention. */
  monthIndex: number;
  isToday: boolean;
  isFuture: boolean;
  sessionCount: number;
};

/** One day of the shown month's record. Days with no sessions may be omitted. */
export type CalendarMonthDay = {
  day: number;
  sessionCount: number;
};

/** What the host gets on a day tap; both shapes map onto a `DaySessionAnchor`. */
export type CalendarDaySelection =
  | { scope: "week"; isoDate: string }
  | { scope: "month"; year: number; monthIndex: number; dayOfMonth: number };

export type CalendarMonthChange = { year: number; monthIndex: number };

/** `{year, monthIndex}` collapsed to one comparable integer, for boundary maths. */
function toYm(year: number, monthIndex: number): number {
  return year * 12 + monthIndex;
}

function daysInMonth(year: number, monthIndex: number): number {
  return new Date(year, monthIndex + 1, 0).getDate();
}

/** Offset of the 1st within a Monday-first grid (0 = the month starts on Monday). */
function mondayOffset(year: number, monthIndex: number): number {
  return (new Date(year, monthIndex, 1).getDay() + 6) % 7;
}

function ChevronLeft() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M15 5l-7 7 7 7" />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 5l7 7-7 7" />
    </svg>
  );
}

function MonthGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3.5" y="5" width="17" height="15" rx="2.5" />
      <path d="M3.5 10h17M8.5 3v3.5M15.5 3v3.5" />
    </svg>
  );
}

function WeekGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3.5" y="8.5" width="17" height="7" rx="2.5" />
    </svg>
  );
}

/**
 * Month names come from `Intl`, not from locale JSON. They are locale data, not
 * product copy — twelve hand-maintained keys per language would drift and add
 * nothing a well-supported browser API already gets right. 2026 is an arbitrary
 * anchor year: only the month component is formatted.
 *
 * TWO forms, because capitalisation is a LANGUAGE rule that `Intl` already
 * encodes: ES yields "enero", EN yields "January". `inline` is the raw output for
 * mid-sentence use ("12 de enero", "January 12"); `title` capitalises for the
 * heading, which the delivered `strings.jsx` does in BOTH locales (`months: ['Enero',
 * …]`) — a deliberate product-copy call, not a grammar one.
 */
function useMonthNames(): { title: string[]; inline: string[] } {
  const { i18n } = useTranslation("dashboard");
  const lang = i18n.language;
  return useMemo(() => {
    const fmt = new Intl.DateTimeFormat(lang, {
      month: "long",
      timeZone: "UTC",
    });
    const inline = Array.from({ length: 12 }, (_, m) =>
      fmt.format(new Date(Date.UTC(2026, m, 1))),
    );
    return { title: inline.map((n) => n.charAt(0).toLocaleUpperCase(lang) + n.slice(1)), inline };
  }, [lang]);
}

export interface CalendarProps {
  /** The seven columns, Monday→Sunday, already bucketed in the member's zone. */
  weekDays: CalendarWeekDay[];
  /** The shown month's record. Empty is a legitimate month, not a missing one. */
  days: CalendarMonthDay[];
  /** The RECORD is in flight — the dates are not, so the grid keeps its geometry. */
  loading: boolean;
  /**
   * The record for this month could not be retrieved. Distinct from `loading`
   * (which resolves) and from an empty month (which is a fact) — saying "no days
   * with a session" when we simply do not know would be a lie about her record.
   */
  unavailable?: boolean;
  /** Ask the host for this month again. Renders the retry control when present. */
  onRetry?: () => void;
  /** A retry is in flight. The control stays MOUNTED and goes disabled — see below. */
  retryPending?: boolean;
  shownYear: number;
  /** 0-indexed, JS `Date` convention. */
  shownMonth: number;
  onChangeMonth: (next: CalendarMonthChange) => void;
  onSelectDay: (selection: CalendarDaySelection) => void;
  /** `"YYYY-MM"` of her earliest completion — the back boundary. `null` = no record yet. */
  firstActivityMonth: string | null;
  showMonth: boolean;
  onToggleMonth: (next: boolean) => void;
}

export function Calendar({
  weekDays,
  days,
  loading,
  unavailable = false,
  onRetry,
  retryPending = false,
  shownYear,
  shownMonth,
  onChangeMonth,
  onSelectDay,
  firstActivityMonth,
  showMonth,
  onToggleMonth,
}: CalendarProps) {
  const { t } = useTranslation("dashboard");
  const months = useMonthNames();
  // Locally unique: `id` is page-global, and this is a reusable `ui/` primitive —
  // two calendars on one page would otherwise collide on the same literal.
  //
  // ONE id serves both bodies because the week strip and the month body are
  // strictly mutually exclusive: the early `return` below renders the strip and
  // nothing else, and the month return never renders the strip. They cannot be
  // in the document together, so `aria-controls` is never ambiguous.
  const bodyId = useId();
  const dayLetters = t("week_days", { returnObjects: true }) as string[];

  // Today is always inside the current week strip, so the strip is the honest
  // source for "which month is the present one" — no extra prop, no second clock
  // that can disagree with the marks the member is looking at. If no column
  // claims today (a caller passing a historical week), the shown month becomes
  // the forward wall: navigating INTO the future is the one thing that must
  // never happen, so the conservative fallback is the correct one.
  const todayEntry = weekDays.find((d) => d.isToday) ?? null;
  const currentYm = todayEntry
    ? toYm(todayEntry.year, todayEntry.monthIndex)
    : toYm(shownYear, shownMonth);

  const firstYm = useMemo(() => {
    if (!firstActivityMonth) return null;
    const [y, m] = firstActivityMonth.split("-").map(Number);
    return Number.isFinite(y) && Number.isFinite(m) ? toYm(y, m - 1) : null;
  }, [firstActivityMonth]);

  const shownYm = toYm(shownYear, shownMonth);
  const isCurrentMonth = shownYm === currentYm;
  /** Day-of-month of "today" only when the shown month IS the current one. */
  const todayDay = isCurrentMonth && todayEntry ? todayEntry.day : null;

  const weekDoneCount = weekDays.filter((d) => d.sessionCount > 0).length;

  const toggle = (
    // T972 — a synchronous view toggle keeps its own chip (structural, like a tab), not
    // the action geometry: `unstyled` routes through the primitive with `.cal-toggle` only.
    <Button
      variant="unstyled"
      className="cal-toggle"
      type="button"
      aria-expanded={showMonth}
      aria-controls={bodyId}
      onClick={() => onToggleMonth(!showMonth)}
    >
      {showMonth ? <WeekGlyph /> : <MonthGlyph />}
      {showMonth ? t("cal_show_week") : t("cal_show_month")}
    </Button>
  );

  const weekStrip = (
    <div id={bodyId} className="cal-body">
      {/* DOM CONTRACT — DO NOT CHANGE. Every mark emits `data-testid="dv2-week-day-{i}"`
          plus `data-day` / `data-today` / `data-done`, read by
          `tests/e2e/dashboard/timezone-day-boundary.spec.ts`. That suite is the only
          thing that can see a UTC-vs-member-local bucketing regression (T356), and the
          attributes are identical on the `<button>` and `<span>` branches so the
          contract never depends on whether a day happens to be tappable. */}
      <div className="cal-week" role="group" aria-label={t("cal_week_eyebrow")}>
        {weekDays.map((d, i) => {
          const active = d.sessionCount > 0;
          const label = active
            ? t("cal_day_label", {
                count: d.sessionCount,
                d: d.day,
                month: months.inline[d.monthIndex],
              })
            : d.isToday
              ? t("cal_day_label_today", {
                  d: d.day,
                  month: months.inline[d.monthIndex],
                })
              : t("cal_day_label_empty", {
                  d: d.day,
                  month: months.inline[d.monthIndex],
                });
          const contract = {
            "data-testid": `dv2-week-day-${i}`,
            "data-day": d.day,
            "data-today": d.isToday ? "true" : "false",
            "data-done": active ? "true" : "false",
          };
          const markClass = cn(
            "cal-mark",
            active && "is-done",
            d.isToday && "is-today",
            d.isFuture && !active && "is-future",
          );

          return (
            <div key={d.iso} className={cn("cal-col", d.isToday && "is-today")}>
              <span className="cal-dow" aria-hidden="true">
                {dayLetters[i]}
              </span>
              {active ? (
                <Button
                  variant="unstyled"
                  type="button"
                  className={markClass}
                  {...contract}
                  aria-label={label}
                  onClick={() => onSelectDay({ scope: "week", isoDate: d.iso })}
                >
                  {d.day}
                </Button>
              ) : (
                // A day with no session is NOT tappable: a tap must not open an
                // empty sheet, so there is no button to press in the first place.
                <span
                  className={markClass}
                  {...contract}
                  role="img"
                  aria-label={label}
                >
                  {d.day}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* The week's count lives on the record line, the same place the month's
          does — never beside the eyebrow, where two mono runs collide. */}
      <p className="cal-record">
        {weekDoneCount > 0 ? (
          <b>{t("cal_week_fact", { count: weekDoneCount })}</b>
        ) : (
          t("cal_week_fact_none")
        )}
      </p>
    </div>
  );

  const head = (
    <div className="cal-head">
      <span className="cal-eyebrow">{t("cal_eyebrow")}</span>
      {toggle}
    </div>
  );

  if (!showMonth) {
    return (
      <div className="cal cal-panel">
        {head}
        {weekStrip}
      </div>
    );
  }

  const atStart = firstYm !== null && shownYm <= firstYm;
  const atEnd = shownYm >= currentYm;

  const step = (delta: number) => {
    const next = shownYm + delta;
    if (firstYm !== null && next < firstYm) return;
    if (next > currentYm) return;
    onChangeMonth({ year: Math.floor(next / 12), monthIndex: next % 12 });
  };

  const monthLength = daysInMonth(shownYear, shownMonth);
  const pad = mondayOffset(shownYear, shownMonth);
  const byDay = new Map(days.map((d) => [d.day, d.sessionCount]));
  const cells: ReactNode[] = [];

  for (let i = 0; i < pad; i += 1) {
    cells.push(
      <span key={`pad-${i}`} className="cal-cell is-pad" aria-hidden="true" />,
    );
  }

  for (let day = 1; day <= monthLength; day += 1) {
    // The dates of a month are known without a server; only the RECORD travels.
    // So a loading grid keeps its geometry and drops its marks — a skeleton with
    // discs would lie about days we do not know yet.
    if (loading) {
      cells.push(
        <span key={day} className="cal-cell is-sk" aria-hidden="true">
          {day}
        </span>,
      );
      continue;
    }

    const sessionCount = byDay.get(day) ?? 0;
    const active = sessionCount > 0;
    const isToday = todayDay === day;
    const isFuture = todayDay !== null && day > todayDay;
    const cellClass = cn(
      "cal-cell",
      active ? "is-active" : isFuture ? "is-future" : "is-empty",
      isToday && "is-today",
    );
    const label = active
      ? t("cal_day_label", {
          count: sessionCount,
          d: day,
          month: months.inline[shownMonth],
        })
      : isToday
        ? t("cal_day_label_today", { d: day, month: months.inline[shownMonth] })
        : t("cal_day_label_empty", { d: day, month: months.inline[shownMonth] });

    cells.push(
      active ? (
        <Button
          variant="unstyled"
          key={day}
          type="button"
          className={cellClass}
          data-day={day}
          data-today={isToday ? "true" : "false"}
          data-done="true"
          aria-label={label}
          onClick={() =>
            onSelectDay({
              scope: "month",
              year: shownYear,
              monthIndex: shownMonth,
              dayOfMonth: day,
            })
          }
        >
          <span className="cal-disc">{day}</span>
        </Button>
      ) : (
        <span
          key={day}
          className={cellClass}
          data-day={day}
          data-today={isToday ? "true" : "false"}
          data-done="false"
          role="img"
          aria-label={label}
        >
          {isToday ? <span className="cal-disc">{day}</span> : day}
        </span>
      ),
    );
  }

  const recordCount = days.filter((d) => d.sessionCount > 0).length;
  const monthName = months.inline[shownMonth];

  return (
    <div className={cn("cal cal-panel", loading && "is-loading")}>
      {head}
      {/* The panel the toggle's `aria-controls` points at, so the button and the
          region it expands are related explicitly rather than by proximity. */}
      <div id={bodyId} className="cal-month-body">
        <div className="cal-nav-wrap">
          <div className="cal-nav">
            <Button
              variant="unstyled"
              type="button"
              className="cal-nav-btn"
              // T972 — while the month loads the arrow refuses taps but keeps focus (the
              // title's live region says «Cargando…»); only the boundary disables it.
              disabled={atStart}
              aria-disabled={loading || undefined}
              aria-label={t("cal_prev_month")}
              onClick={() => step(-1)}
            >
              <ChevronLeft />
            </Button>
            <h3 className="cal-nav-label" aria-live="polite">
              {months.title[shownMonth]}
              <span className="cal-nav-year">
                {loading ? t("cal_loading") : shownYear}
              </span>
            </h3>
            <Button
              variant="unstyled"
              type="button"
              className="cal-nav-btn"
              disabled={atEnd}
              aria-disabled={loading || undefined}
              aria-label={t("cal_next_month")}
              onClick={() => step(1)}
            >
              <ChevronRight />
            </Button>
          </div>
          {/* ONLY the back boundary is explained. Knowing where your record
              starts is information; "this is the current month" sitting under
              the title on every single visit is noise — being in today's month
              is the default state, not news. */}
          {/* The note does NOT interpolate the month: it is already in the title
              one line above, and repeating it forced a hand-capitalisation — the
              trap R-T407-7 exists for. It also has to match which boundary this
              actually is. Ours is `firstActivityMonth`, so it says where she
              first trained; "you started in <month>" read as the join month and
              was simply false. When the boundary becomes the join month, the
              copy for that is `atStart` in the handoff's `strings.jsx`. */}
          {atStart && firstYm !== null && !loading ? (
            <p className="cal-boundary">{t("cal_at_start")}</p>
          ) : null}
        </div>

        <div>
          <div className="cal-dows" aria-hidden="true">
            {dayLetters.map((letter, i) => (
              <span key={`dow-${i}`}>{letter}</span>
            ))}
          </div>
          <div
            className={cn("cal-grid", loading && "is-loading")}
            role="group"
            aria-label={t("cal_grid_label", {
              month: months.inline[shownMonth],
              year: shownYear,
            })}
            aria-busy={loading ? "true" : "false"}
            style={{ marginTop: "var(--tfc-gap-sm)" }}
          >
            {cells}
          </div>
        </div>

        {/* A STABLE live region, not a live paragraph. When she presses an arrow
            focus stays on the arrow and only this line changes, so without it the
            loading → resolved (or → failed) transition is silent. The wrapper is
            always in the DOM and the content is inserted INTO it, which is what
            makes the announcement reliable — a region that appears together with
            its own text is the classic version screen readers miss. */}
        <div role="status" aria-live="polite">
          {!loading ? (
            unavailable ? (
              // We do not know this month. Saying "no days with a session" here
              // would invent an empty record; the grid above already shows bare
              // dates, and this line says why they carry no marks.
              //
              // It states the failure AND offers the way out. An earlier pass
              // said "try again in a moment" with nothing to press — a promise
              // the UI could not keep — and removing the sentence left a dead
              // end instead. The control is the honest version of both.
              <p className="cal-record is-empty is-unavailable">
                {t("cal_record_unavailable")}
                {onRetry ? (
                  // Stays mounted AND focusable across the retry. Unmounting it
                  // would drop focus to <body> and lose a keyboard member's place
                  // at the exact moment she acted — which is why the host holds
                  // `unavailable` true until the retry resolves.
                  //
                  // `aria-disabled`, NOT `disabled`: disabling a focused element
                  // blurs it, so the real attribute would drop focus to <body>
                  // just as surely as unmounting. Inertness comes from dropping
                  // the handler.
                  <Button
                    variant="text"
                    className="is-inline"
                    type="button"
                    aria-disabled={retryPending || undefined}
                    isLoading={retryPending}
                    disabledMode="aria"
                    onClick={retryPending ? undefined : onRetry}
                  >
                    {t("cal_record_retry")}
                  </Button>
                ) : null}
              </p>
            ) : recordCount === 0 ? (
              <p className="cal-record is-empty">
                {isCurrentMonth
                  ? t("cal_record_empty_current")
                  : t("cal_record_empty", { month: monthName })}
              </p>
            ) : (
              <p className="cal-record">
                <b>
                  {t("cal_record", { count: recordCount, month: monthName })}
                </b>{" "}
                <span>{t("cal_record_detail")}</span>
              </p>
            )
          ) : null}
        </div>
      </div>
    </div>
  );
}
